import { useState } from "react";
import Logo from "../components/Logo";
import { useTheme } from "../hooks/useAppShell";
import { Sun, Moon } from "lucide-react";

export default function Login() {
  const { theme, toggle: toggleTheme } = useTheme();
  const [email, setEmail] = useState("");
  const [step, setStep] = useState("enter"); // 'enter' | 'logout-then-signin'
  const [err, setErr] = useState("");

  const buildAuthUrl = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    return `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const validEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);

  const handleSignIn = (e) => {
    e.preventDefault();
    const clean = email.trim().toLowerCase();
    if (!validEmail(clean)) {
      setErr("Please enter a valid email address");
      return;
    }
    setErr("");
    // Save the intended email so it's remembered after the Google logout hop
    try { sessionStorage.setItem("ye_intended_email", clean); } catch (err) { console.warn("sessionStorage unavailable:", err); }
    // Go directly to Emergent auth. If Google auto-picks the wrong account
    // and it's not whitelisted, the AuthCallback screen catches it.
    window.location.href = buildAuthUrl();
  };

  const openGoogleLogout = () => {
    window.open("https://accounts.google.com/Logout", "_blank", "noopener,noreferrer");
    setStep("logout-then-signin");
  };

  const continueAfterLogout = () => {
    const clean = email.trim().toLowerCase();
    if (!validEmail(clean)) {
      setErr("Please enter a valid email address");
      setStep("enter");
      return;
    }
    try { sessionStorage.setItem("ye_intended_email", clean); } catch (err) { console.warn("sessionStorage unavailable:", err); }
    window.location.href = buildAuthUrl();
  };

  return (
    <div className="grain" style={{ minHeight: "100vh", display: "grid", placeItems: "center", padding: 24, position: "relative", overflow: "hidden" }}>
      {/* Theme toggle in top-right corner */}
      <button
        onClick={toggleTheme}
        data-testid="theme-toggle-login"
        aria-label="Toggle theme"
        title={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
        className="btn"
        style={{
          position: "absolute", top: 20, right: 20, zIndex: 5,
          padding: "8px 12px", borderRadius: 999,
        }}
      >
        {theme === "dark" ? <Sun size={14} /> : <Moon size={14} />}
      </button>
      {/* Ambient background orbs */}
      <div style={{ position: "absolute", top: "-20%", left: "-10%", width: 600, height: 600, background: "radial-gradient(circle, rgba(124,108,255,0.28), transparent 70%)", filter: "blur(80px)", pointerEvents: "none" }} />
      <div style={{ position: "absolute", bottom: "-20%", right: "-10%", width: 600, height: 600, background: "radial-gradient(circle, rgba(76,232,206,0.18), transparent 70%)", filter: "blur(80px)", pointerEvents: "none" }} />
      <div className="panel" style={{ padding: 44, maxWidth: 460, width: "100%", textAlign: "center", position: "relative", zIndex: 2 }}>
        <div style={{ display: "flex", justifyContent: "center", marginBottom: 14 }}>
          <Logo size={120} />
        </div>
        <div className="muted" style={{ marginBottom: 32, fontSize: 11, letterSpacing: "0.28em", fontWeight: 600 }}>OPERATIONS OS · v1.6</div>
        <h1 style={{ fontSize: 26, marginBottom: 12 }}>Welcome to YE Music</h1>
        <p className="muted" style={{ marginBottom: 24, fontSize: 13.5, lineHeight: 1.65 }}>
          Enter your team email, then verify with Google. Only whitelisted addresses can sign in.
        </p>

        <form onSubmit={handleSignIn} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <label style={{ textAlign: "left" }}>
            <span className="muted" style={{ display: "block", marginBottom: 6, fontSize: 11.5, letterSpacing: "0.06em", textTransform: "uppercase", fontWeight: 600 }}>
              Email address
            </span>
            <input
              className="input"
              type="email"
              autoFocus
              autoComplete="email"
              placeholder="you@yourseventfully.com"
              value={email}
              onChange={(e) => { setEmail(e.target.value); if (err) setErr(""); }}
              data-testid="login-email-input"
              style={{ width: "100%", padding: "13px 14px", fontSize: 14, borderRadius: 12 }}
            />
          </label>
          {err && (
            <div data-testid="login-email-error" style={{ color: "var(--danger)", fontSize: 12, textAlign: "left" }}>{err}</div>
          )}
          <button
            type="submit"
            data-testid="google-login-btn"
            className="btn btn-primary"
            style={{ width: "100%", justifyContent: "center", padding: "14px 18px", fontSize: 14, borderRadius: 12, marginTop: 4 }}
          >
            Continue with Google
          </button>
        </form>

        {step === "enter" && (
          <>
            <div style={{ margin: "18px 0 12px", display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ flex: 1, height: 1, background: "var(--border)" }} />
              <span className="muted" style={{ fontSize: 10.5, letterSpacing: "0.14em", fontWeight: 600 }}>STUCK ON THE WRONG ACCOUNT?</span>
              <div style={{ flex: 1, height: 1, background: "var(--border)" }} />
            </div>
            <button
              type="button"
              onClick={openGoogleLogout}
              data-testid="switch-google-account-btn"
              className="btn"
              style={{
                width: "100%",
                justifyContent: "center",
                padding: "11px 16px",
                fontSize: 12.5,
                borderRadius: 12,
                background: "transparent",
                border: "1px solid var(--border)",
              }}
            >
              Sign out of Google first
            </button>
            <p className="muted" style={{ marginTop: 12, fontSize: 11, lineHeight: 1.55 }}>
              Use this if Google keeps auto-picking the wrong account. It opens Google's sign-out page in a new tab — sign out there, then come back.
            </p>
          </>
        )}

        {step === "logout-then-signin" && (
          <div
            data-testid="switch-account-instructions"
            className="panel"
            style={{
              marginTop: 18,
              padding: 16,
              textAlign: "left",
              background: "rgba(124,108,255,0.06)",
              border: "1px solid rgba(124,108,255,0.32)",
              borderRadius: 12,
              fontSize: 12.5,
              lineHeight: 1.65,
            }}
          >
            <div style={{ fontWeight: 700, marginBottom: 8, fontSize: 13 }}>Finish signing out:</div>
            <ol style={{ paddingLeft: 18, margin: "0 0 12px 0", color: "var(--muted)" }}>
              <li>In the Google tab that just opened, click <b>"Sign out"</b> (or "Sign out of all accounts").</li>
              <li>Close that tab.</li>
              <li>Come back here and click the button below.</li>
            </ol>
            <button
              type="button"
              onClick={continueAfterLogout}
              data-testid="continue-after-logout-btn"
              className="btn btn-primary"
              style={{ width: "100%", justifyContent: "center", padding: "12px 16px", fontSize: 13, borderRadius: 10 }}
            >
              I've signed out — continue with Google
            </button>
            <button
              type="button"
              onClick={openGoogleLogout}
              data-testid="switch-account-reopen"
              className="btn"
              style={{ width: "100%", justifyContent: "center", padding: "9px 14px", fontSize: 11.5, borderRadius: 10, marginTop: 8, background: "transparent", border: "1px solid var(--border)" }}
            >
              Didn't open? Reopen Google sign-out
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
